import { useMemo } from 'react';
import useSWR from 'swr';
import { convertISeriesResponseToSeriesOverview } from '~/common/converter/series/series';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { filterNgTags } from '~/utils/ng-keywords';
import { logError } from '~/utils/utils';
import { getIsAccepted } from '@app/_hooks/use-is-accepted-sensitive';
import { buildSeries } from '@app/_repositories/query-builder';
import { getQueriesForClient } from '@app/_repositories/query-service';
const ITEMS = 10;

const fetchRecommendedSeriesFromSeries = async (seriesId: string) => {
  const recommendedSeriesRequestId = 'recommended-series-from-series';
  const isSensitiveAccepted = getIsAccepted();
  try {
    const response = await getQueriesForClient({
      request_list: [
        {
          request_id: `recommended-series-from-series`,
          series_request: {
            id: seriesId,
            require_series: {
              require_recommended_series: {
                require_series_page: {
                  require_series_list: {
                    ...buildSeries(),
                    require_recommender_model_id: true,
                    require_can_show_google_ad: true,
                    require_total_like_count: true,
                  },
                  page: {
                    limit: ITEMS,
                  },
                },
                script_type_filter: {
                  exclude_video: true,
                  exclude_audio: true,
                },
              },
            },
          },
        },
      ],
    });
    const recommendedSeriesList =
      response.response_list.find(
        (res) => res.request_id === recommendedSeriesRequestId
      )?.series?.recommended_series_page?.series_list ?? [];

    return isSensitiveAccepted
      ? recommendedSeriesList?.map((se) =>
          convertISeriesResponseToSeriesOverview(se)
        )
      : recommendedSeriesList
          ?.filter((se) => se.is_sensitive === false)
          .map((se) => convertISeriesResponseToSeriesOverview(se));
  } catch (error: any) {
    logError(error);
    return [];
  }
};

export const useLocalHooks = (seriesId: string, ngKeywords: string[]) => {
  const { session } = useTellerSession();
  const {
    data: series,
    isLoading,
    isValidating,
  } = useSWR(
    session?.uid
      ? ['/series/recommended-series-by-series', seriesId, session?.uid]
      : null,
    ([_, sId, __]) => fetchRecommendedSeriesFromSeries(sId)
  );

  const showLoading = series === undefined || isLoading || isValidating;

  const seriesList = useMemo(
    () =>
      (series ?? []).map((series) => {
        return {
          ...series,
          tags: filterNgTags(series.tags, ngKeywords),
        };
      }),
    [ngKeywords, series]
  );

  return {
    seriesList,
    showLoading,
    showSensitive: session?.tellerUser?.showSensitive ?? false,
  };
};
